
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Lock, User, AlertCircle, ArrowLeft, Check, FileText } from 'lucide-react'
import { Button, H2, Body, ButtonVariant, INPUT_MASKS, Input } from '@meiuca/ui-components'
import { isValidCPF, cpfDigitsOnly } from '@meiuca/web-foundation'

const registrationSchema = z.object({
  name: z.string()
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(50, 'Nome deve ter no máximo 50 caracteres'),
  // CPF obrigatório — backend valida o algoritmo de dígitos verificadores.
  // Replica a regra no cliente pra feedback imediato.
  cpf: z.string()
    .min(1, 'CPF é obrigatório')
    .refine((value) => isValidCPF(value), 'CPF inválido. Confira os números.'),
  password: z.string()
    .min(8, 'Senha deve ter pelo menos 8 caracteres')
    .regex(/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Senha deve conter pelo menos: 1 letra minúscula, 1 maiúscula e 1 número'),
  confirmPassword: z.string()
    .min(1, 'Confirmação de senha é obrigatória'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
})

type RegistrationDetailsData = z.infer<typeof registrationSchema>

interface RegistrationDetailsStepProps {
  phone: string
  onDetailsSubmit: (data: RegistrationDetailsData) => void
  onBack: () => void
  isLoading?: boolean
  error?: string | null
}

export function RegistrationDetailsStep({
  phone,
  onDetailsSubmit,
  onBack,
  isLoading = false,
  error
}: RegistrationDetailsStepProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegistrationDetailsData>({
    resolver: zodResolver(registrationSchema),
  })

  // Wrap submit pra normalizar o CPF (tirar máscara) antes de propagar
  // pro AuthContext / authService — backend grava só dígitos.
  const handleNormalizedSubmit = handleSubmit((data) => {
    onDetailsSubmit({ ...data, cpf: cpfDigitsOnly(data.cpf) })
  })

  // Format phone for display
  const formatPhoneDisplay = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`
    }
    return phone
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="text-center mb-8">
        <motion.div
          className="w-16 h-16 bg-gradient-to-br from-brand-secondary to-brand-primary rounded-2xl mx-auto mb-6 flex items-center justify-center"
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <span className="text-2xl font-bold text-white">M</span>
        </motion.div>
        
        <H2 className="mb-2">Quase lá!</H2>
        <Body className="text-text-secondary mb-2">
          Complete seu cadastro
        </Body>
        
        {/* Phone confirmation */}
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-background-positive border border-green-200 rounded-full">
          <Check className="w-4 h-4 text-positive" />
          <Body className="text-positive text-sm font-medium">
            {formatPhoneDisplay(phone)}
          </Body>
        </div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-background-negative border border-red-200 rounded-lg flex items-center gap-3"
        >
          <AlertCircle className="w-5 h-5 text-negative flex-shrink-0" />
          <Body className="text-negative text-sm">{error}</Body>
        </motion.div>
      )}

      <form onSubmit={handleNormalizedSubmit} className="space-y-6">
        {/* Name Field */}
        <Input
          {...register('name')}
          type="text"
          label="Nome completo *"
          placeholder="Seu nome completo"
          leftIcon={User}
          error={!!errors.name}
          errorMessage={errors.name?.message}
          required
          fullWidth
          className="w-full"
          autoFocus
        />

        {/* CPF Field — obrigatório pra cobrança via Asaas */}
        <Input
          {...register('cpf')}
          type="tel"
          inputMode="numeric"
          label="CPF *"
          placeholder="000.000.000-00"
          leftIcon={FileText}
          mask={INPUT_MASKS.CPF}
          error={!!errors.cpf}
          errorMessage={errors.cpf?.message}
          required
          fullWidth
          className="w-full"
        />

        {/* Password Field */}
        <Input
          {...register('password')}
          type={showPassword ? 'text' : 'password'}
          label="Senha *"
          placeholder="••••••••"
          leftIcon={Lock}
          trailingButton={{
            icon: showPassword ? EyeOff : Eye,
            onClick: () => setShowPassword(!showPassword),
            'aria-label': showPassword ? 'Ocultar senha' : 'Mostrar senha'
          }}
          error={!!errors.password}
          errorMessage={errors.password?.message}
          required
          fullWidth
          className="w-full"
        />

        {/* Confirm Password Field */}
        <Input
          {...register('confirmPassword')}
          type={showConfirmPassword ? 'text' : 'password'}
          label="Confirmar senha *"
          placeholder="••••••••"
          leftIcon={Lock}
          trailingButton={{
            icon: showConfirmPassword ? EyeOff : Eye,
            onClick: () => setShowConfirmPassword(!showConfirmPassword),
            'aria-label': showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'
          }}
          error={!!errors.confirmPassword}
          errorMessage={errors.confirmPassword?.message}
          required
          fullWidth
          className="w-full"
        />

        {/* Terms and Privacy */}
        <div className="text-sm text-text-secondary">
          Ao criar uma conta, você concorda com nossos{' '}
          <a href="/termos" className="text-brand-primary hover:text-brand-secondary transition-colors">
            Termos de Uso
          </a>{' '}
          e{' '}
          <a href="/privacidade" className="text-brand-primary hover:text-brand-secondary transition-colors">
            Política de Privacidade
          </a>.
        </div>

        {/* Submit Button */}
        <Button
          variant={ButtonVariant.PRIMARY}
          type="submit"
          loading={isLoading}
          className="w-full"
        >
          {isLoading ? 'Criando conta...' : 'Criar conta'}
        </Button>

        {/* Back Button */}
        <div className="text-center">
          <Button
            variant={ButtonVariant.PRIMARY}
            buttonStyle="clear"
            onClick={onBack}
            disabled={isLoading}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      </form>
    </motion.div>
  )
}